import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit2, FiCamera, FiInstagram, FiCalendar } from 'react-icons/fi';
import { FaTiktok, FaFacebook } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { uploadApi } from '../../utils/api';
import './ProfilePage.css';

// Mappings for Hebrew labels
const categoryLabels = {
  fashion: 'אופנה',
  beauty: 'יופי',
  lifestyle: 'לייפסטייל',
  food: 'אוכל',
  travel: 'טיולים',
  fitness: 'כושר',
  tech: 'טכנולוגיה',
  gaming: 'גיימינג',
  parenting: 'הורות',
  business: 'עסקים',
  art: 'אמנות',
  music: 'מוזיקה',
  comedy: 'קומדיה',
  education: 'חינוך',
  pets: 'חיות מחמד'
};

const platformLabels = {
  instagram: 'אינסטגרם',
  tiktok: 'טיקטוק',
  facebook: 'פייסבוק'
};

const genderLabels = {
  male: 'זכר',
  female: 'נקבה',
  other: 'אחר',
  prefer_not_to_say: 'לא צוין'
};

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      fullName: user?.fullName || '',
      displayName: user?.displayName || '',
      phone: user?.phone || '',
      city: user?.city || '',
      gender: user?.gender || '',
      birthYear: user?.birthYear || '',
      instagramUsername: user?.instagramUsername || '',
      tiktokUsername: user?.tiktokUsername || ''
    }
  });

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName || '',
        displayName: user.displayName || '',
        phone: user.phone || '',
        city: user.city || '',
        gender: user.gender || '',
        birthYear: user.birthYear || '',
        instagramUsername: user.instagramUsername || '',
        tiktokUsername: user.tiktokUsername || ''
      });
    }
  }, [user, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await updateProfile({
        fullName: data.fullName,
        displayName: data.displayName,
        phone: data.phone,
        city: data.city,
        gender: data.gender,
        birthYear: data.birthYear ? parseInt(data.birthYear) : null,
        instagramUsername: data.instagramUsername,
        tiktokUsername: data.tiktokUsername
      });
      toast.success('הפרופיל עודכן בהצלחה!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      await uploadApi.uploadProfile(file);
      toast.success('התמונה עודכנה בהצלחה!');
      window.location.reload(); // Refresh to show new image
    } catch (error) {
      toast.error('שגיאה בהעלאת התמונה');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>הפרופיל שלי</h1>
        <p>עדכן את הפרטים האישיים שלך</p>
      </div>

      <div className="profile-grid">
        {/* Profile Card */}
        <motion.div
          className="profile-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Profile Image */}
          <div className="profile-image-section">
            <div className="profile-image">
              {user?.profileImage ? (
                <img src={user.profileImage} alt={user.displayName || user.fullName} />
              ) : (
                <span className="avatar-placeholder">
                  {(user?.displayName || user?.fullName || user?.email)?.charAt(0)?.toUpperCase()}
                </span>
              )}
              <label className="image-upload-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  hidden
                />
                <FiCamera />
              </label>
            </div>
            <h2>{user?.displayName || user?.fullName || 'משתמש'}</h2>
            <span className="role-badge">
              {user?.role === 'creator' ? 'יוצר/ת תוכן' : user?.role === 'admin' ? 'מנהל' : 'מותג'}
            </span>
          </div>

          {/* Social Stats */}
          {user?.role === 'creator' && (
            <div className="social-stats">
              {user?.instagramUsername && (
                <div className="social-stat">
                  <FiInstagram className="social-icon instagram" />
                  <span>@{user.instagramUsername}</span>
                </div>
              )}
              {user?.tiktokUsername && (
                <div className="social-stat">
                  <FaTiktok className="social-icon tiktok" />
                  <span>@{user.tiktokUsername}</span>
                </div>
              )}
            </div>
          )}

          {/* Platforms */}
          {user?.preferredPlatforms?.length > 0 && (
            <div className="categories-section">
              <h4>פלטפורמות</h4>
              <div className="categories-list">
                {user.preferredPlatforms.map((platform, i) => (
                  <span key={i} className="category-badge platform-badge">
                    {platformLabels[platform] || platform}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          {(user?.categories?.length > 0 || user?.customCategories?.length > 0) && (
            <div className="categories-section">
              <h4>קטגוריות</h4>
              <div className="categories-list">
                {user.categories?.map((cat, i) => (
                  <span key={i} className="category-badge">
                    {categoryLabels[cat] || cat}
                  </span>
                ))}
                {user.customCategories?.map((cat, i) => (
                  <span key={`custom-${i}`} className="category-badge custom">
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Edit Form */}
        <motion.div
          className="profile-form-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="card-header">
            <h3>פרטים אישיים</h3>
            {!isEditing && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setIsEditing(true)}
              >
                <FiEdit2 />
                עריכה
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FiUser className="label-icon" />
                  שם מלא
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.fullName ? 'error' : ''}`}
                  disabled={!isEditing}
                  {...register('fullName')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FiUser className="label-icon" />
                  שם תצוגה
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.displayName ? 'error' : ''}`}
                  disabled={!isEditing}
                  {...register('displayName')}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiMail className="label-icon" />
                אימייל
              </label>
              <input
                type="email"
                className="form-input"
                value={user?.email || ''}
                disabled
              />
              <span className="form-hint">לא ניתן לשנות את כתובת האימייל</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FiPhone className="label-icon" />
                  טלפון
                </label>
                <input
                  type="tel"
                  className="form-input"
                  disabled={!isEditing}
                  {...register('phone')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FiMapPin className="label-icon" />
                  עיר מגורים
                </label>
                <input
                  type="text"
                  className="form-input"
                  disabled={!isEditing}
                  {...register('city')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">מגדר</label>
                <select
                  className="form-input"
                  disabled={!isEditing}
                  {...register('gender')}
                >
                  <option value="">בחר</option>
                  <option value="male">זכר</option>
                  <option value="female">נקבה</option>
                  <option value="other">אחר</option>
                  <option value="prefer_not_to_say">מעדיפ/ה לא לציין</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FiCalendar className="label-icon" />
                  שנת לידה
                </label>
                <input
                  type="number"
                  className="form-input"
                  disabled={!isEditing}
                  min="1950"
                  max="2010"
                  {...register('birthYear')}
                />
              </div>
            </div>

            {user?.role === 'creator' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <FiInstagram className="label-icon" />
                    אינסטגרם
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    disabled={!isEditing}
                    placeholder="שם משתמש"
                    {...register('instagramUsername')}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FaTiktok className="label-icon" />
                    טיקטוק
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    disabled={!isEditing}
                    placeholder="שם משתמש"
                    {...register('tiktokUsername')}
                  />
                </div>
              </div>
            )}

            {isEditing && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleCancel}
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'שומר...' : 'שמור שינויים'}
                </button>
              </div>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
